import { Injectable } from '@angular/core';
import { Contingency, ContingencyBlock, ContingencyCategory } from '@shared/models/contingency.model';
import { Drug } from '@shared/models/drug.model';
import { CaracteristicAnalysis, GroupAnalysis, PhycotropeAnalysis } from '@shared/models/group.model';
import { Pathology, SubPathology } from '@shared/models/pathology.model';
import { Posology, Prevalence, Value } from '@shared/models/posology.model';
import cdf from '@stdlib/stats-base-dists-chisquare-cdf';
import { Analyse, Khi2Table, Khi2Trust } from '../models/analyse.model';
import { PathologyType } from '../models/pathology.enum';
import { Patient, Prescription } from '../models/patient.model';

@Injectable()
export class ProcessService {
  private psycos: Array<string> = [];
  private antecedants: Array<string> = [];
  private khi2Table: Array<Khi2Table> = [
    //{ trust: Khi2Trust.Correlation90, threshold: 2.706 },
    { trust: Khi2Trust.Correlation950, threshold: 3.841 },
    //{ trust: Khi2Trust.Correlation975, threshold: 5.024 },
    { trust: Khi2Trust.Correlation990, threshold: 6.635 },
    { trust: Khi2Trust.Correlation999, threshold: 10.828 }
  ];
  private timeRef = 1735689600;
  private week = 604800;
  private population = 4010;

  private hasDrugPresent(psyco: string, patient: Patient, drugs: { [key: string]: Drug }): boolean {
    for (let p = 0; p < patient.prescriptions.length; p++) {
      const prescription = patient.prescriptions[p];
      const drug = drugs[prescription.name.toUpperCase().trim()];
      if (!drug) {
        console.log('invalid drug name : ', prescription.name.toUpperCase().trim());
        continue;
      }
      if (drug.atc.includes(psyco)) {
        return true;
      }
    }
    return false;
  }

  public processPathologyContingency = (
    data: Array<Patient>,
    drugs: { [key: string]: Drug },
    pathologies: { [key: string]: PathologyType }
  ): Array<Contingency> => {
    const psycos = [
      ...new Set(Object.values(drugs).map(drug => drug.atc.substring(0, 4))) //replace(/[0-9]+$/i, '').replace(/[A-Z]+$/i, '')
    ];
    //const pathoTypes = Object.keys(PathologyType).filter(key => isNaN(+key));
    // pathology restricted after first analysis, keep only revelent ones after a broad scan.
    const pathoTypes = ['Neuro', 'psychiatrique', 'cancerologie', 'Démence'];
    const contigencies: Array<Contingency> = [];

    psycos.forEach(psyco => {
      const contingency = new Contingency('Contingence des antécédents et des ' + psyco, psyco);
      pathoTypes.forEach(condition => {
        contingency.categories.push(new ContingencyCategory(condition));
      });
      data.forEach(patient => {
        const psycoMatching = patient.prescriptions.filter(pres =>
          drugs[pres.name.toUpperCase().trim()].atc.includes(psyco)
        );
        const psycoMatchingUnique = psycoMatching.filter(
          (item, i, array) =>
            array
              .map(item => drugs[item.name.toUpperCase().trim()].atc)
              .indexOf(drugs[item.name.toUpperCase().trim()].atc) === i
        );
        // fill subtest data blocks
        psycoMatchingUnique.forEach(matching => {
          const drug = drugs[matching.name.toUpperCase().trim()];
          if (!contingency.subTests.find(sub => sub.code === drug.atc)) {
            contingency.subTests.push({
              code: drug.atc,
              name: drug.type
            });
            contingency.categories.forEach(categories => {
              categories.subTests.push(new ContingencyBlock());
            });
            contingency.total.subTests.push(new ContingencyBlock());
          }
        });
        // get indexes to fill adequate categories
        const indexesToFill = psycoMatchingUnique.map(pres =>
          contingency.subTests.findIndex(sub => sub.code === drugs[pres.name.toUpperCase().trim()].atc)
        );
        // loop over categories to fill matching ones
        contingency.categories.forEach(categories => {
          const antecedent = categories.name;
          //const hasCondition = patient.antecedents.includes(antecedent);
          let hasCondition = false;
          patient.antecedents.forEach(ante => {
            if (pathologies[ante.toUpperCase().trim()]) {
              const type = pathologies[ante.toUpperCase().trim()].toString();
              if (type === antecedent) {
                hasCondition = true;
              }
            } else if (ante.toUpperCase() !== 'X') {
              console.log('antecedant not found : ', ante);
            }
          });

          if (hasCondition) {
            // only add information if condition is present, in control or tested depending on psyco presences
            if (indexesToFill.length === 0) {
              categories.control.count++;
            } else {
              categories.test.count++;
              indexesToFill.forEach(index => {
                categories.subTests[index].count++;
              });
            }
          }
        });
        // and fill totals
        if (indexesToFill.length === 0) {
          contingency.total.control.count++;
        } else {
          contingency.total.test.count++;
          indexesToFill.forEach(index => {
            contingency.total.subTests[index].count++;
          });
        }
      });
      this.computeContingency(contingency);

      this.khi2Contigency(contingency);

      contigencies.push(contingency);
    });

    // console.log('Final analysis');
    const ordered = contigencies.sort((a, b) => b.total.test.count - a.total.test.count);

    return ordered;
  };

  public processCaracteristicContingency = (
    data: Array<Patient>,
    drugs: { [key: string]: Drug }
  ): Array<Contingency> => {
    const psycos = [
      ...new Set(Object.values(drugs).map(drug => drug.atc.substring(0, 4))) //replace(/[0-9]+$/i, '').replace(/[A-Z]+$/i, '')
    ];
    const caracteristicGroups = [
      { name: 'Homme ', field: 'gender', range: [0, 0] },
      { name: 'Femme ', field: 'gender', range: [1, 1] },
      { name: 'pas de Polymédication', field: 'drugs', range: [0, 4] },
      { name: 'Polymédication', field: 'drugs', range: [5, 9] },
      { name: 'Hyper Polymédication', field: 'drugs', range: [10, 99] },
      { name: 'Soixantenaire', field: 'age', range: [60, 69] },
      { name: 'Septantenaire', field: 'age', range: [70, 79] },
      { name: 'Huitantenaire', field: 'age', range: [80, 89] },
      { name: 'Nonantenaire', field: 'age', range: [90, 99] },
      { name: 'Centenaire', field: 'age', range: [100, 999] },
      { name: 'Institutionnalisation courte', field: 'duration', range: [0, 3] },
      { name: 'Institutionnalisation moyenne ', field: 'duration', range: [4, 9] },
      { name: 'Institutionnalisation longue ', field: 'duration', range: [10, 99] },
      { name: "Pas d'hospitalisation Recente", field: 'hospi', range: [0, 0] },
      { name: 'Hospitalisation Recente', field: 'hospi', range: [1, 1] },
      { name: 'Secteur conventionnel', field: 'protected', range: [0, 0] },
      { name: 'Secteur protégé', field: 'protected', range: [1, 1] },
      { name: 'GIR 1', field: 'gir', range: [1, 1] },
      { name: 'GIR 2', field: 'gir', range: [2, 2] },
      { name: 'GIR 3', field: 'gir', range: [3, 3] },
      { name: 'GIR 4', field: 'gir', range: [4, 4] },
      { name: 'GIR 5+', field: 'gir', range: [5, 9] }
    ];

    const contigencies: Array<Contingency> = [];

    psycos.forEach(psyco => {
      const contingency = new Contingency('Contingence des caractéristiques patients et des ' + psyco, psyco);
      caracteristicGroups.forEach(carac => {
        contingency.categories.push(new ContingencyCategory(carac.name, carac.field));
      });
      data.forEach(patient => {
        const psycoMatching = patient.prescriptions.filter(pres =>
          drugs[pres.name.toUpperCase().trim()].atc.includes(psyco)
        );
        const psycoMatchingUnique = psycoMatching.filter(
          (item, i, array) =>
            array
              .map(item => drugs[item.name.toUpperCase().trim()].atc)
              .indexOf(drugs[item.name.toUpperCase().trim()].atc) === i
        );
        // fill subtest data blocks
        psycoMatchingUnique.forEach(matching => {
          const drug = drugs[matching.name.toUpperCase().trim()];
          if (!contingency.subTests.find(sub => sub.code === drug.atc)) {
            contingency.subTests.push({
              code: drug.atc,
              name: drug.type
            });
            contingency.categories.forEach(categories => {
              categories.subTests.push(new ContingencyBlock());
            });
            contingency.total.subTests.push(new ContingencyBlock());
          }
        });
        // get indexes to fill adequate categories
        const indexesToFill = psycoMatchingUnique.map(pres =>
          contingency.subTests.findIndex(sub => sub.code === drugs[pres.name.toUpperCase().trim()].atc)
        );
        // loop over categories to fill matching ones
        for (let c = 0; c < caracteristicGroups.length; c++) {
          const caracteritic = caracteristicGroups[c];
          const category = contingency.categories[c];
          // }
          //contingency.categories.forEach(categorieshgfhgdf => {
          const antecedent = category.name;
          //const hasCondition = patient.antecedents.includes(antecedent);
          const hasCaracteristic =
            patient[caracteritic.field] >= caracteritic.range[0] &&
            patient[caracteritic.field] <= caracteritic.range[1];

          if (hasCaracteristic) {
            // only add information if condition is present, in control or tested depending on psyco presences
            if (indexesToFill.length === 0) {
              category.control.count++;
            } else {
              category.test.count++;
              indexesToFill.forEach(index => {
                category.subTests[index].count++;
              });
            }
          }
        }
        // and fill totals
        if (indexesToFill.length === 0) {
          contingency.total.control.count++;
        } else {
          contingency.total.test.count++;
          indexesToFill.forEach(index => {
            contingency.total.subTests[index].count++;
          });
        }
      });
      this.computeContingency(contingency);

      this.khi2Contigency(contingency);

      contigencies.push(contingency);
    });

    // console.log('Final analysis');
    const ordered = contigencies.sort((a, b) => b.total.test.count - a.total.test.count);

    return ordered;
  };

  public processCaracteristicAnalysis = (
    data: Array<Patient>,
    drugs: { [key: string]: Drug },
    pathologies: { [key: string]: PathologyType }
  ): CaracteristicAnalysis => {
    const psycos = [
      ...new Set(Object.values(drugs).map(drug => drug.atc.substring(0, 4))) //replace(/[0-9]+$/i, '').replace(/[A-Z]+$/i, '')
    ].sort((a, b) => a.localeCompare(b));
    const caracteristicGroups = [
      { name: 'Male', field: 'gender', range: [0, 0] },
      { name: 'Female', field: 'gender', range: [1, 1] },
      { name: 'No Polypharmacy', field: 'drugs', range: [0, 9] },
      { name: 'Hyper Polypharmacy', field: 'drugs', range: [10, 99] },
      { name: 'Sixty', field: 'age', range: [60, 69] },
      { name: 'Seventy', field: 'age', range: [70, 79] },
      { name: 'Eighty', field: 'age', range: [80, 89] },
      { name: 'Ninety', field: 'age', range: [90, 99] },
      { name: 'Centenary', field: 'age', range: [100, 999] },
      { name: 'Short Institutionalization', field: 'duration', range: [0, 3] },
      { name: 'Medium Institutionalization', field: 'duration', range: [4, 9] },
      { name: 'Long Institutionalization', field: 'duration', range: [10, 99] },
      { name: 'No Recent Hospitalization', field: 'hospi', range: [0, 0] },
      { name: 'Recent Hospitalization', field: 'hospi', range: [1, 1] },
      { name: 'Classic Unit', field: 'protected', range: [0, 0] },
      { name: 'Protected Unit', field: 'protected', range: [1, 1] },
      { name: 'GIR 1', field: 'gir', range: [1, 1] },
      { name: 'GIR 2', field: 'gir', range: [2, 2] },
      { name: 'GIR 3', field: 'gir', range: [3, 3] },
      { name: 'GIR 4', field: 'gir', range: [4, 4] },
      { name: 'GIR 5+', field: 'gir', range: [5, 9] },
      { name: 'Dementia', field: 'atcd', range: [], source: 'Démence' },
      { name: 'Psychiatric', field: 'atcd', range: [], source: 'psychiatrique' },
      { name: 'Cancer', field: 'atcd', range: [], source: 'cancerologie' },
      { name: 'Neurologic', field: 'atcd', range: [], source: 'Neuro' }
    ];

    const caracteritics: Array<{ field: string; name: string }> = [
      { field: 'age', name: 'Age' },
      { field: 'gender', name: 'Gender' },
      { field: 'drugs', name: 'Hyperpolypharmacy ' },
      { field: 'gir', name: 'Level of dependency' },
      { field: 'protected', name: 'Protected unit' },
      { field: 'atcd', name: 'Medical history' }
    ];
    const analysis: CaracteristicAnalysis = {
      psychotropes: psycos,
      caracteristics: []
    };

    caracteritics.forEach(carac => {
      const caracAnalysis: GroupAnalysis = {
        field: carac.field,
        name: carac.name,
        groupNames: caracteristicGroups.filter(group => group.field === carac.field).map(group => group.name),
        groupPatients: [],
        psychotropes: []
      };
      if (carac.field === 'atcd') {
        caracAnalysis.groupPatients = caracAnalysis.groupNames.map(name => {
          const group = caracteristicGroups.find(group => group.name === name);
          const pathologyRange = Object.keys(pathologies).filter(key => pathologies[key].toString() === group.source);
          return data.filter(patient =>
            patient.antecedents.some(atcd => pathologyRange.includes(atcd.toUpperCase().trim()))
          );
        });
      } else {
        caracAnalysis.groupPatients = caracAnalysis.groupNames.map(name => {
          const group = caracteristicGroups.find(group => group.name === name);
          return data.filter(
            patient => patient[carac.field] >= group.range[0] && patient[carac.field] <= group.range[1]
          );
        });
      }

      psycos.forEach(psyco => {
        const psycoAnalysis: PhycotropeAnalysis = {
          pValue: 1,
          data: []
        };
        caracAnalysis.groupPatients.forEach((group, g) => {
          let matchCount = 0;
          group.forEach(patient => {
            const psycoMatching = patient.prescriptions.filter(pres =>
              drugs[pres.name.toUpperCase().trim()].atc.includes(psyco)
            );
            const psycoMatchingUnique = psycoMatching.filter(
              (item, i, array) =>
                array
                  .map(item => drugs[item.name.toUpperCase().trim()].atc)
                  .indexOf(drugs[item.name.toUpperCase().trim()].atc) === i
            );
            if (psycoMatchingUnique.length > 0) {
              matchCount++;
            }
          });
          psycoAnalysis.data.push({
            name: caracAnalysis.groupNames[g],
            count: matchCount,
            ratio: matchCount / group.length,
            size: group.length
          });
        });

        this.khi2Analysis(psycoAnalysis);
        caracAnalysis.psychotropes.push(psycoAnalysis);
      });
      analysis.caracteristics.push(caracAnalysis);
    });

    // console.log('Final analysis');
    //const ordered = contigencies.sort((a, b) => b.total.test.count - a.total.test.count);

    return analysis;
  };

  private khi2Analysis = (psycoAnalysis: PhycotropeAnalysis): void => {
    const totalCount = psycoAnalysis.data.reduce((acc, group) => acc + group.size, 0);
    const ratioTest = psycoAnalysis.data.reduce((acc, group) => acc + group.count, 0) / totalCount;
    const ratioControl = psycoAnalysis.data.reduce((acc, group) => acc + (group.size - group.count), 0) / totalCount;
    const testGap = psycoAnalysis.data.reduce(
      (acc, group) => acc + Math.pow(group.count - ratioTest * group.size, 2) / (ratioTest * group.size),
      0
    );
    const controlGap = psycoAnalysis.data.reduce(
      (acc, group) =>
        acc + Math.pow(group.size - group.count - ratioControl * group.size, 2) / (ratioControl * group.size),
      0
    );
    const khi2Squared = testGap + controlGap;
    psycoAnalysis.pValue = 1 - cdf(khi2Squared, psycoAnalysis.data.length - 1);
  };

  public processConditionsATC = (data: Array<Patient>, drugs: { [key: string]: Drug }): Array<Analyse> => {
    const analysis = [];
    const psycos = [
      ...new Set(Object.values(drugs).map(drug => drug.atc.substring(0, 4))) //replace(/[0-9]+$/i, '').replace(/[A-Z]+$/i, '')
    ];

    const conditions = ['gir', 'age', 'duration', 'gender', 'perma', 'drugs', 'hospi', 'protected'];

    conditions.forEach(condition => {
      psycos.forEach(psyco => {
        const analyse: Analyse = {
          name: condition + '-' + psyco,
          tested: 0,
          controlled: 0,
          test: 0,
          control: 0
        };
        data.forEach(patient => {
          if (patient[condition] || patient[condition] === 0) {
            if (this.hasDrugPresent(psyco, patient, drugs)) {
              analyse.tested++;
              analyse.test += patient[condition];
            } else {
              analyse.controlled++;
              analyse.control += patient[condition];
            }
          }
        });

        this.runAnalyse(analyse);

        analysis.push(analyse);
      });
    });

    // console.log('Final analysis');
    const ordered = analysis.sort((a, b) => a.name.localeCompare(b.name));
    // console.log(
    //   ordered.map((order) => {
    //     return { name: order.name, observation: order.difference + '%', detail: order };
    //   })
    // );
    return ordered;
  };

  public processConditions = (data: Array<Patient>): Array<Analyse> => {
    const analysis = [];
    this.psycos = [];
    for (const key in data[0]) {
      if (key.startsWith('pr_')) {
        this.psycos.push(key);
      }
    }

    const conditions = ['gir', 'age', 'duration', 'gender', 'perma', 'drugs', 'hospi', 'protected'];

    conditions.forEach(condition => {
      this.psycos.forEach(psyco => {
        const analyse: Analyse = {
          name: condition + '-' + psyco,
          tested: 0,
          controlled: 0,
          test: 0,
          control: 0
        };
        data.forEach(patient => {
          if (patient[condition] || patient[condition] === 0) {
            if (patient[psyco]) {
              analyse.tested++;
              analyse.test += patient[condition];
            } else {
              analyse.controlled++;
              analyse.control += patient[condition];
            }
          }
        });

        this.runAnalyse(analyse);

        analysis.push(analyse);
      });
    });

    // console.log('Final analysis');
    const ordered = analysis.sort((a, b) => a.name.localeCompare(b.name));
    // console.log(
    //   ordered.map((order) => {
    //     return { name: order.name, observation: order.difference + '%', detail: order };
    //   })
    // );
    return ordered;
  };

  public processPathologies = (data: Array<Patient>, pathologies: { [key: string]: PathologyType }): Array<Analyse> => {
    const analysis = [];
    this.psycos = [];
    for (const key in data[0]) {
      if (key.startsWith('pr_')) {
        this.psycos.push(key);
      }
    }
    const pathoTypes = Object.keys(PathologyType).filter(key => isNaN(+key));

    pathoTypes.forEach(pathoType => {
      this.psycos.forEach(psyco => {
        const analyse: Analyse = {
          name: pathoType + '-' + psyco,
          tested: 0,
          controlled: 0,
          test: 0,
          control: 0
        };
        data.forEach(patient => {
          let hasPatho = false;
          patient.antecedents.forEach(antecedant => {
            if (pathologies[antecedant.toUpperCase().trim()]) {
              const type = pathologies[antecedant.toUpperCase().trim()].toString();
              if (type === pathoType) {
                hasPatho = true;
              }
            } else if (antecedant.toUpperCase() !== 'X') {
              console.log('antecedant not found : ', antecedant);
            }
          });
          if (patient[psyco]) {
            analyse.tested++;
            analyse.test += hasPatho ? 1 : 0;
          } else {
            analyse.controlled++;
            analyse.control += hasPatho ? 1 : 0;
          }
        });

        // if (pathoType === 'Démence') {
        //   debugger;
        // }

        if (analyse.control !== 0) {
          this.runAnalyse(analyse);

          analysis.push(analyse);
        }
      });
    });

    // console.log('Final analysis');
    const ordered = analysis.sort((a, b) => a.name.localeCompare(b.name));
    // console.log(
    //   ordered.map((order) => {
    //     return { name: order.name, observation: order.difference + '%', detail: order };
    //   })
    // );
    return ordered;
  };

  private runAnalyse = (analyse: Analyse): void => {
    const generalValue = analyse.test + analyse.control;
    const batchSize = analyse.tested + analyse.controlled;
    const idealValue = generalValue / batchSize;
    const testGap = Math.pow(analyse.test - idealValue * analyse.tested, 2) / (idealValue * analyse.tested);
    const controlGap =
      Math.pow(analyse.control - idealValue * analyse.controlled, 2) / (idealValue * analyse.controlled);
    const khi2Tests = [];
    for (const test of this.khi2Table) {
      const independance = testGap + controlGap < test.threshold;
      khi2Tests.push({
        trust: test.trust,
        valid: !independance
      });
    }

    analyse.test /= analyse.tested;
    analyse.control /= analyse.controlled;
    analyse.difference = Math.round(((analyse.test - analyse.control) / analyse.control) * 100);
    analyse.khi2Tests = khi2Tests;
  };

  private khi2Contigency = (contingency: Contingency): void => {
    contingency.categories.forEach(category => {
      const ratioTest = contingency.total.test.count / contingency.total.total.count;
      const ratioControl = contingency.total.control.count / contingency.total.total.count;
      const testGap =
        Math.pow(category.test.count - ratioTest * category.total.count, 2) / (ratioTest * category.total.count);
      const controlGap =
        Math.pow(category.control.count - ratioControl * category.total.count, 2) /
        (ratioControl * category.total.count);
      const khi2Tests = [];
      const khi2Squared = testGap + controlGap;
      for (const test of this.khi2Table) {
        const independance = khi2Squared < test.threshold;
        khi2Tests.push({
          trust: test.trust,
          valid: !independance
        });
      }
      category.pValue = 1 - cdf(khi2Squared, 1);
      category.khi2Tests = khi2Tests;
    });
    const ordered = contingency.categories.sort((a, b) => a.pValue - b.pValue);
    const subdivided = ordered.reduce((prev, curr) => {
      if (!prev[curr.field]) {
        prev[curr.field] = [];
      }
      prev[curr.field].push(curr);
      return prev;
    }, {});
    for (const field in subdivided) {
      const fieldCategories = subdivided[field];
      fieldCategories.forEach((category, index) => {
        category.BF = category.pValue * fieldCategories.length < 0.05;
        category.FDR = category.pValue < (0.05 * (index + 1)) / fieldCategories.length;
        category.qvalue = (category.pValue * fieldCategories.length) / (index + 1) < 0.05;
      });
    }
  };

  private processPosology = (type: string, prescriptions: Array<Prescription>): Posology => {
    const posology: Posology = {
      type: type,
      amounts: [],
      count: prescriptions.length
    };
    for (let p = 0; p < 6; p++) {
      posology.amounts.push(this.ComputeValue(prescriptions.map(pos => (pos.posology[p] > 0 ? 1 : 0))));
    }
    posology.systematic = this.ComputeValue(prescriptions.map(pos => (pos.systematic ? 1 : 0)));
    posology.duration = this.ComputeValue(
      prescriptions.map(pos => Math.max(0, (this.timeRef - pos.start) / this.week))
    );
    return posology;
  };

  public processPosologies = (data: Array<Patient>, drugs: { [key: string]: Drug }): Array<Posology> => {
    const posologiesByType = {};
    const posologies = [];

    data.forEach(patient => {
      patient.prescriptions.forEach(prescription => {
        const atc = drugs[prescription.name.toUpperCase().trim()]?.atc;
        const type = atc?.substring(0, 4);
        if (posologiesByType[type]) {
          posologiesByType[type].all.push(prescription);
        } else {
          posologiesByType[type] = { all: [prescription] };
        }
        if (posologiesByType[type][atc]) {
          posologiesByType[type][atc].push(prescription);
        } else {
          posologiesByType[type][atc] = [prescription];
        }
      });
    });

    for (const type in posologiesByType) {
      const posologyArray = posologiesByType[type].all;
      const mainPosology = this.processPosology(type, posologyArray);
      mainPosology.molecules = [];

      for (const atc in posologiesByType[type]) {
        if (atc !== 'all') {
          const atcArray = posologiesByType[type][atc];
          const atcPosology = this.processPosology(atc, atcArray);
          mainPosology.molecules.push(atcPosology);
        }
      }

      mainPosology.molecules.sort((a, b) => a.type.localeCompare(b.type));

      posologies.push(mainPosology);
    }

    return posologies.sort((a, b) => a.type.localeCompare(b.type));
  };

  public processPrevalences = (data: Array<Patient>, drugs: { [key: string]: Drug }): Array<Prevalence> => {
    const prevalences = [];
    const presentTypes = [];
    const psycos = [
      ...new Set(Object.values(drugs).map(drug => drug.atc.substring(0, 4))) //replace(/[0-9]+$/i, '').replace(/[A-Z]+$/i, '')
    ];

    // prepare patien prescriptions
    const patients = data.map(p => {
      return {
        ...p,
        codes: p.prescriptions
          .map(pres => drugs[pres.name.toUpperCase().trim()].atc)
          .filter((psyco, p, array) => array.indexOf(psyco) === p)
      };
    });

    // const patients = data.map(patient => {
    //   const presences = [];
    //   patient.prescriptions.forEach(prescription => {
    //     if (!presences.includes(prescription.type)) {
    //       presences.push(prescription.type);
    //     }
    //     if (!presentTypes.includes(prescription.type)) {
    //       presentTypes.push(prescription.type);
    //     }
    //   });
    //   return presences;
    // });

    const global: Prevalence = {
      type: 'Tous' //DrugType[type]
    };
    global.amount = this.ComputeValue(patients.map(patients => (patients.codes.length > 0 ? 1 : 0)));
    prevalences.push(global);

    psycos.forEach((type: string) => {
      const prevalence: Prevalence = {
        type: type //DrugType[type]
      };
      prevalence.amount = this.ComputeValue(
        patients.map(patients => (patients.codes.map(code => code.substring(0, 4)).includes(type) ? 1 : 0))
      );
      prevalences.push(prevalence);
    });

    prevalences.forEach(prevalence => {
      prevalence.rangeTotal = [
        Math.round(
          (prevalence.amount.mean + (1.96 * prevalence.amount.sigma) / Math.pow(patients.length, 0.5)) * this.population
        ),
        Math.max(
          Math.round(prevalence.amount.mean * patients.length),
          Math.round(
            (prevalence.amount.mean - (1.96 * prevalence.amount.sigma) / Math.pow(patients.length, 0.5)) *
              this.population
          )
        )
      ];
      prevalence.rangePercent = prevalence.rangeTotal.map(amount => amount / this.population);
    });

    this.bonusLogs(data);

    return prevalences.sort((a, b) => b.amount.mean - a.amount.mean);
  };

  private ComputeValue(values: Array<number>): Value {
    const ordered = values.sort((a, b) => a - b);
    const mediane = ordered[Math.round((values.length - 1) / 2)];
    const firstQ = ordered[Math.round((values.length - 1) / 4)];
    const lastQ = ordered[Math.round(((values.length - 1) / 4) * 3)];
    const min = ordered[0];
    const max = ordered[values.length - 1];
    const mean = values.reduce((prev, curr) => curr + prev, 0) / values.length;
    const sigma2 = values.reduce((prev, curr) => Math.pow(curr - mean, 2) + prev, 0) / values.length;
    const sigma = Math.pow(sigma2, 0.5);
    return { mean, sigma, mediane, firstQ, lastQ, min, max };
  }

  private computeContingency(contengency: Contingency): void {
    const bothTotal = contengency.total.control.count + contengency.total.test.count; //10
    contengency.categories.forEach(category => {
      const bothCount = category.control.count + category.test.count; //14
      category.control.inCategory = category.control.count / bothCount; //17
      category.test.inCategory = category.test.count / bothCount; //16
      category.total.inCategory = 1;
      category.control.inTest = category.control.count / contengency.total.control.count; //20
      category.test.inTest = category.test.count / contengency.total.test.count; //19
      category.total.inTest = bothCount / bothTotal; //15
      category.control.total = category.control.count / bothTotal; //23
      category.test.total = category.test.count / bothTotal; //22
      category.total.total = bothCount / bothTotal; //15
      category.total.count = bothCount; //14
      category.subTests.forEach((sub, s) => {
        sub.inCategory = sub.count / (category.test.count || 1); //18
        sub.inTest = sub.count / (contengency.total.subTests[s].count || 1); //21
        sub.total = sub.count / bothTotal; //24
      });
    });
    contengency.total.control.inCategory = contengency.total.control.count / bothTotal; //12
    contengency.total.test.inCategory = contengency.total.test.count / bothTotal; //11
    contengency.total.total.inCategory = 1;
    contengency.total.control.inTest = 1;
    contengency.total.test.inTest = 1;
    contengency.total.total.inTest = 1;
    contengency.total.control.total = contengency.total.control.count / bothTotal; //12
    contengency.total.test.total = contengency.total.test.count / bothTotal; //11
    contengency.total.total.total = 1;
    contengency.total.total.count = bothTotal; // 10
    contengency.total.subTests.forEach((sub, s) => {
      sub.inCategory = sub.count / contengency.total.test.count;
      sub.inTest = 1;
      sub.total = sub.count / contengency.total.test.count;
    });
    contengency.categories = contengency.categories.filter(cat => cat.total.count > 0);
  }

  public processPathologyOccurence = (
    data: Array<Patient>,
    pathologies: { [key: string]: PathologyType }
  ): Array<Pathology> => {
    const occurences = [];
    const pathoTypes = Object.keys(PathologyType).filter(key => isNaN(+key));

    pathoTypes.forEach(pathoType => {
      const occurence = {
        type: PathologyType[pathoType],
        occurence: 0,
        presence: 0,
        subTypes: []
      } as Pathology;
      data.forEach(patient => {
        const matching = [];
        patient.antecedents.forEach(antecedant => {
          if (pathologies[antecedant.toUpperCase().trim()]) {
            const type = pathologies[antecedant.toUpperCase().trim()].toString();
            if (type === pathoType) {
              matching.push(antecedant.toUpperCase().trim());
            }
          } else if (antecedant.toUpperCase() !== 'X') {
            console.log('antecedant not found : ', antecedant);
          }
        });
        if (matching.length > 0) {
          occurence.occurence++;
        }
        matching.forEach(match => {
          let subType = occurence.subTypes.find(sub => sub.name === match);
          if (subType) {
            subType.occurence++;
          } else {
            subType = {
              name: match,
              occurence: 1,
              subPresence: 0,
              totalPresence: 0
            } as SubPathology;
            occurence.subTypes.push(subType);
          }
        });
      });
      occurence.presence = occurence.occurence / data.length;
      occurence.subTypes.forEach(sub => {
        sub.subPresence = sub.occurence / occurence.occurence;
        sub.totalPresence = sub.occurence / data.length;
      });
      occurence.subTypes = occurence.subTypes.sort((a, b) => b.occurence - a.occurence);
      occurences.push(occurence);
    });
    return occurences.filter(occ => occ.occurence > 0).sort((a, b) => b.occurence - a.occurence);
  };

  private bonusLogs(data): void {
    // only for log, bonus value
    const ratio: Prevalence = { type: 'ratio' };
    ratio.amount = this.ComputeValue(data.map(patient => patient.prescriptions.length / (patient.drugs || 1)));
    ratio.rangeTotal = [
      Math.round((ratio.amount.mean + (1.96 * ratio.amount.sigma) / Math.pow(data.length, 0.5)) * this.population),
      Math.max(
        Math.round(ratio.amount.mean * data.length),
        Math.round((ratio.amount.mean - (1.96 * ratio.amount.sigma) / Math.pow(data.length, 0.5)) * this.population)
      )
    ];
    ratio.rangePercent = ratio.rangeTotal.map(amount => amount / this.population);
    const psyco: Prevalence = { type: 'psyco' };
    psyco.amount = this.ComputeValue(data.map(patient => patient.prescriptions.length));
    psyco.rangeTotal = [
      Math.round((psyco.amount.mean + (1.96 * psyco.amount.sigma) / Math.pow(data.length, 0.5)) * this.population),
      Math.max(
        Math.round(psyco.amount.mean * data.length),
        Math.round((psyco.amount.mean - (1.96 * psyco.amount.sigma) / Math.pow(data.length, 0.5)) * this.population)
      )
    ];
    psyco.rangePercent = psyco.rangeTotal.map(amount => amount / this.population);
    const drugs: Prevalence = { type: 'drugs' };
    drugs.amount = this.ComputeValue(data.map(patient => patient.drugs));
    drugs.rangeTotal = [
      Math.round((drugs.amount.mean + (1.96 * drugs.amount.sigma) / Math.pow(data.length, 0.5)) * this.population),
      Math.max(
        Math.round(drugs.amount.mean * data.length),
        Math.round((drugs.amount.mean - (1.96 * drugs.amount.sigma) / Math.pow(data.length, 0.5)) * this.population)
      )
    ];
    drugs.rangePercent = drugs.rangeTotal.map(amount => amount / this.population);

    // 'prevalence de prescription de psychotropes : ' +
    // (data.reduce((prev, curr) => prev + curr.prescriptions.length, 0) /
    //   data.reduce((prev, curr) => prev + curr.drugs, 0)) *
    //   100 +
    // '%'
    // data.reduce((prev, curr) => prev + curr.prescriptions.length, 0) / data.length,
    // data.reduce((prev, curr) => prev + curr.drugs, 0) / data.length

    console.log(ratio, psyco, drugs);
  }
}
